import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import ContactForm from '../../components/ContactForm';
import { fetchLocalizedCollection } from '../../lib/cms';

export default function SparePartsPageEn() {
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) => getSlugVal(s) === 'reservedele' || getSlugVal(s) === 'spare-parts'
      );
      if (match) setPageData(match);
    });
  }, []);

  const title = pageData?.titel || 'Original spare parts for PMB & industrial machinery';
  const badge = pageData?.undertitel || 'Original Components & Stock';
  const intro =
    pageData?.intro ||
    'When a machine requires replacement wear parts or critical components, precision fit and material quality are paramount to continuous uptime. We maintain an extensive warehouse of original spare parts for PMB eccentric presses, hydraulic systems, central lubrication, and sheet metal working machinery.';
  const seoTitle =
    pageData?.seo_titel || 'PMB Spare Parts – Original Spare Parts for Presses & Machinery';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'Original spare parts for PMB presses and associated manufacturing equipment. Fast delivery of clutches, seals, filters, controls, and wear components.';
  const image =
    pageData && pageData.billede
      ? Array.isArray(pageData.billede)
        ? pageData.billede[0]
        : pageData.billede
      : 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/reservedele.png';

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={metaDesc}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">Products</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">Spare Parts</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
                {badge}
              </span>
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {title}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
                {intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-4 text-sm font-semibold">
                <a
                  href="#order"
                  className="bg-[#006368] hover:bg-[#005155] text-white px-6 py-3 rounded-lg shadow-sm transition"
                >
                  Inquire about spare parts
                </a>
                <a
                  href="tel:+4557526262"
                  className="bg-stone-800 hover:bg-stone-700 text-stone-200 px-6 py-3 rounded-lg border border-stone-700 transition flex items-center gap-2"
                >
                  <i className="fa-solid fa-phone" /> +45 57 52 62 62
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-stone-700 shadow-2xl bg-stone-800">
                <img
                  src={image}
                  alt="PMB Original Spare Parts"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories of spare parts */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900">
              What can we supply?
            </h2>
            <p className="mt-2 text-stone-600 text-sm sm:text-base">
              With our extensive historical documentation and in-house machining facility in Borup, we supply both catalog items from stock and custom-machined replacement parts from engineering drawings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-gear" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Press Components</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Pneumatic clutches, brake friction linings, pitman screws, connecting rods, bronze bushings, and main bearings for PMB eccentric presses.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-droplet" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Lubrication Systems</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Pumps, progressive divider blocks, high-pressure tubing, compression fittings, cycle monitoring switches, and certified lubricants from SKF, Lincoln & Vogel.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-microchip" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Control & Electrical</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Safety relays, two-hand operation consoles, rotary cam switches, proximity sensors, optical light curtains, and PLC components.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-[#006368]/10 text-[#006368] flex items-center justify-center mb-4">
                <i className="fa-solid fa-scissors" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Wear Parts & Tooling</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Guillotine shear blades, standard press brake punches and dies, hydraulic seals, wiper rings, and mechanical slide guides.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Custom manufacturing callout */}
      <section className="py-12 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#006368]/10 border border-[#006368]/20 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-[#006368]">
                Custom Machine Workshop
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Do you have an older machine where parts are discontinued?
              </h3>
              <p className="text-sm text-stone-600 max-w-2xl">
                In our workshop in Borup, we have the lathes, milling equipment, and historical design archives to manufacture replacement components from physical samples or original mechanical drawings.
              </p>
            </div>
            <a
              href="tel:+4557526262"
              className="bg-[#006368] hover:bg-[#005155] text-white font-semibold text-sm px-6 py-3 rounded-lg transition whitespace-nowrap shrink-0"
            >
              Call our technicians
            </a>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="order" className="py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-[#006368] block mb-2">
              Direct Warehouse Request
            </span>
            <h2 className="text-3xl font-extrabold text-stone-900">
              Inquire about parts & components
            </h2>
            <p className="mt-2 text-sm text-stone-600">
              Please include machine model, serial number, and part number if available, so we can verify compatibility.
            </p>
          </div>

          <ContactForm defaultSubject="Inquiry regarding spare parts" lang="en" />
        </div>
      </section>
    </>
  );
}
